import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';

import { Reveal } from '@/components/atoms';
import { ConsultationForm, ConsultationNextSteps } from '@/components/consultation';
import { SectionHeading } from '@/components/molecules';
import { Section } from '@/components/organisms';
import { anchorOffsetSx } from '@/theme';

import { request, REQUEST_ID, REQUEST_TITLE_ID } from '../content';
import { AdviceAssurance } from '../molecules';

/**
 * The request as two columns: the form, and everything that makes it safe to
 * fill in.
 *
 * The same arrangement the quotation page settled on, for the same reason.
 * The rail is read *while* the form is filled rather than after it: "free, and
 * not conditional on buying" and "we use your details to send the advice and
 * nothing else" are the two sentences that decide whether the submit gets
 * pressed, and printed underneath it they arrive after the decision.
 *
 * The split is 7/5 — the form needs the width for two fields on a row, the
 * rail needs enough for four numbered steps to read as steps — and it
 * collapses to one column below `md`, where the form comes first in the DOM so
 * a phone opens on the thing the page is asking for rather than on four
 * paragraphs about it.
 *
 * The form is five fields now rather than eight, so it no longer runs much
 * taller than the rail beside it. The rail stays sticky anyway: it costs
 * nothing when the two are a similar height, and the band has to survive the
 * form growing a validation message under every field.
 *
 * The band carries the intro's anchor, and `anchorOffsetSx` with it: the
 * header is fixed and the promise ribbon above it can be dismissed, so the
 * offset has to be the variable the ribbon publishes rather than a number.
 *
 * No state lives here. The form's is its own, the rail reads config and
 * content, and this band only decides where the two columns sit.
 */
export function AdviceRequestBand() {
  return (
    <Section id={REQUEST_ID} labelledBy={REQUEST_TITLE_ID} pattern="grid" sx={anchorOffsetSx()}>
      <SectionHeading
        id={REQUEST_TITLE_ID}
        eyebrow={request.eyebrow}
        title={request.title}
        lede={request.lede}
      />

      <Grid container spacing={{ xs: 8, md: 10 }} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 7 }}>
          <Reveal>
            <ConsultationForm />
          </Reveal>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          {/* Outside the `Reveal`: a sticky element can only travel inside its
              own containing block, and `Reveal` is a box that wraps its
              content, so sticking the rail within it would give it no room to
              move. Out here the block is the grid item, which stretches to the
              height of the form beside it. */}
          <Box sx={{ position: { md: 'sticky' }, top: { md: 120 } }}>
            <Reveal direction="right">
              <Stack spacing={6}>
                <ConsultationNextSteps />
                <AdviceAssurance />
              </Stack>
            </Reveal>
          </Box>
        </Grid>
      </Grid>
    </Section>
  );
}
